"use client";
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import { Sparkles, Star, ArrowRight, Check, ChevronDown, ChevronUp, Zap, Brain, Camera, TrendingUp, Shield, Users, Award, Play, Menu, X, Scissors, Droplets, Shirt, Dumbbell } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BEFORE_AFTERS = [
{
  name: 'Marcus T.',
  age: 26,
scoreBefore: 5.8,
scoreAfter: 8.4,
  improvement: '9.7/10',
  tag: 'Grooming + General Style + Hairstyle',
beforeImg: "/assets/images/Screenshot_2026-03-22_010828-1774134558014.png",
afterImg: "/assets/images/Screenshot_2026-03-22_010828-1774134558014.png",
beforeAlt: 'Young man with casual appearance before glow-up transformation',
afterAlt: 'Confident well-groomed young man after glow-up transformation'
},
{
  name: 'Jordan M.',
  age: 22,
scoreBefore: 5.2,
scoreAfter: 7.9,
  improvement: '8.9/10',
  tag: 'Jawline + Facial Look + Hairstyle',
beforeImg: "/assets/images/Screenshot_2026-03-22_011522-1774134938600.png",
afterImg: "/assets/images/Screenshot_2026-03-22_011522-1774134938600.png",
beforeAlt: 'Man before and after grooming and style upgrade transformation',
afterAlt: 'Well-styled confident man after grooming and style upgrade'
}];


const TESTIMONIALS = [
{ name: 'Alex R.', handle: '@alexr_fit', text: "Went from a 6.1 to 8.4 in 30 days following the roadmap. The hairstyle suggestion alone changed everything. Girls started noticing.", score: '8.4', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14a3770de-1763293843071.png", avatarAlt: 'Profile photo of Alex R. fitness enthusiast' },
{ name: 'Tom B.', handle: '@tombecker', text: "I was skeptical but the jawline exercises + grooming advice genuinely changed my face shape perception. My confidence is through the roof.", score: '9.2', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19b52eabd-1766573425041.png", avatarAlt: 'Profile photo of Tom B. with defined jawline' },
{ name: 'Kai N.', handle: '@kai_nextlevel', text: "Best €20 I\'ve spent this year. The fashion suggestions matched my face shape perfectly. I look 2 years younger and way more put together.", score: '8.2', avatar: "https://images.unsplash.com/photo-1644661459228-c8b1501b8a92", avatarAlt: 'Profile photo of Kai N. in stylish outfit' },
{ name: 'Remi D.', handle: '@remidupont', text: "The AI actually understood my face shape and gave specific hairstyle options. Not generic advice. My dating app matches doubled.", score: '7.6', avatar: "https://images.unsplash.com/photo-1546112617-a34b3c14902d", avatarAlt: 'Profile photo of Remi D. with new hairstyle' },
{ name: 'Jake S.', handle: '@jakesimmons', text: "The skincare routine cleared my skin in 3 weeks. Never thought I\'d care about skincare but the results speak for themselves.", score: '8.0', avatar: "https://images.unsplash.com/photo-1583264277135-6f46aa408484", avatarAlt: 'Profile photo of Jake S. with clear skin' },
{ name: 'Luca M.', handle: '@lucamaxx', text: "The dating profile optimization section is insane. My match rate went up 280% after following the photo angle and bio tips.", score: '8.9', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1101e9026-1763296623238.png", avatarAlt: 'Profile photo of Luca M. smiling confidently' },
{ name: 'Nate V.', handle: '@natevibes', text: "Week 2 of the roadmap and I already feel like a different man. The posture and body language tips are seriously underrated.", score: '8.7', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b33f67ea-1772230123289.png", avatarAlt: 'Profile photo of Nate V. with improved posture' },
{ name: 'Chris P.', handle: '@chrispeaks', text: "I shared my before/after on Reddit and it blew up. Everyone wants to know my routine. LookMax AI gave me the blueprint.", score: '9.0', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b218e9f7-1774024909068.png", avatarAlt: 'Profile photo of Chris P. after transformation' }];


const FEATURES = [
{ icon: Brain, title: 'AI Attractiveness Rating', desc: 'Get a precise 1-10 score based on 47 facial harmony metrics, symmetry, jawline, golden ratio, and more.', color: 'text-violet-400', bg: 'bg-violet-500/10' },
{ icon: Sparkles, title: 'Glow-Up Simulator', desc: 'See your potential after the 30-day roadmap with AI-enhanced visualization of your best self.', color: 'text-amber-400', bg: 'bg-amber-500/10' },
{ icon: Scissors, title: 'Hairstyle Recommendations', desc: 'Get 3 personalized hairstyle options matched to your face shape, jawline, and forehead width.', color: 'text-sky-400', bg: 'bg-sky-500/10' },
{ icon: Droplets, title: 'Skincare Analysis', desc: 'AI detects your skin tone, texture, and concerns to build a no-BS morning and night routine.', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
{ icon: Dumbbell, title: 'Jawline & Physique Tips', desc: 'Targeted exercises and lifestyle changes to sharpen your jawline and improve your overall frame.', color: 'text-rose-400', bg: 'bg-rose-500/10' },
{ icon: Shirt, title: 'Style Guide for Men', desc: 'Clothing recommendations based on your body type, skin tone, and face shape to maximize your look.', color: 'text-purple-400', bg: 'bg-purple-500/10' }];


const FAQS = [
{ q: 'How accurate is the AI attractiveness score?', a: 'Our model analyzes 47 facial harmony metrics including symmetry, golden ratio proportions, jawline definition, and skin quality. The score reflects scientific attractiveness research and is calibrated against millions of data points.' },
{ q: 'What photos work best for analysis?', a: 'Use a well-lit, front-facing photo with no filters. Natural lighting works best. Avoid sunglasses or hats for the most accurate facial analysis.' },
{ q: 'Can I cancel my subscription anytime?', a: 'Yes. You can cancel your subscription at any time from your dashboard. You\'ll retain access until the end of your billing period with no questions asked.' },
{ q: 'Is my photo stored or shared?', a: 'Your photos are encrypted and stored securely. We never share your images with third parties. You can delete your photos and account data at any time from your settings.' },
{ q: 'How long does the analysis take?', a: 'The AI analysis typically completes in 15-30 seconds. We analyze facial symmetry, skin quality, feature proportions, and generate your personalized roadmap in real time.' }];


function CounterAnimation({ end, duration = 2000 }: {end: number;duration?: number;}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const startTime = Date.now();
        const timer = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * end));
          if (progress === 1) clearInterval(timer);
        }, 16);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{count >= 1000 ? `${Math.round(count / 1000)}K` : count.toLocaleString()}</span>;
}

export default function LandingPage() {
  const router = useRouter();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAnalyze = () => {
    router.push('/paywall-screen');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      router.refresh();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#050508] text-white overflow-x-hidden">
      {/* Topbar */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#050508]/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AppLogo size={44} />
            <span className="font-display font-bold text-lg tracking-tight">LookMax AI</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#pricing" className="text-sm text-white/60 hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="text-sm text-white/60 hover:text-white transition-colors">FAQ</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {user ?
            <>
                <button onClick={() => router.push('/user-dashboard')} className="text-sm text-white/70 hover:text-white transition-colors px-4 py-2">
                  Dashboard
                </button>
                <button onClick={handleSignOut} className="text-sm text-white/50 hover:text-white transition-colors px-3 py-2">
                  Sign Out
                </button>
              </> :

            <>
                <button onClick={() => router.push('/login')} className="text-sm text-white/70 hover:text-white transition-colors px-4 py-2">
                  Sign In
                </button>
                <button
                onClick={handleAnalyze}
                className="text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white px-5 py-2 rounded-full transition-all duration-200 active:scale-95">
                  Get Started
                </button>
              </>
            }
          </div>
          <button className="md:hidden text-white/70" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {mobileMenuOpen &&
        <div className="md:hidden bg-[#0a0a10]/95 backdrop-blur-xl border-b border-white/5 px-4 py-4 flex flex-col gap-4">
            <a href="#pricing" className="text-sm text-white/70" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#faq" className="text-sm text-white/70" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            {user ?
          <button onClick={() => router.push('/user-dashboard')} className="bg-violet-600 text-white text-sm font-semibold px-5 py-3 rounded-full w-full">
                Dashboard
              </button> :

          <button onClick={handleAnalyze} className="bg-violet-600 text-white text-sm font-semibold px-5 py-3 rounded-full w-full">
                Analyze My Face
              </button>
          }
          </div>
        }
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-20 pb-16 px-4 overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-950/40 via-[#050508] to-amber-950/20 pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-violet-600/10 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-amber-500/8 blur-[80px] pointer-events-none" />

        {/* Floating score cards */}
        <div className="absolute left-4 top-1/3 hidden lg:block animate-float" style={{ animationDelay: '0s' }}>
          <div className="glass-card rounded-2xl p-4 w-44">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full overflow-hidden">
                <AppImage src="https://img.rocket.new/generatedImages/rocket_gen_img_146683b96-1766758227279.png" alt="Man's avatar showing score improvement" width={32} height={32} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-xs font-semibold text-white">Marcus</p>
                <p className="text-[10px] text-white/40">Just analyzed</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">Score</span>
              <span className="text-lg font-bold gradient-text-gold">8.4/10</span>
            </div>
          </div>
        </div>

        <div className="absolute right-4 top-1/3 hidden lg:block animate-float" style={{ animationDelay: '2s' }}>
          <div className="glass-card rounded-2xl p-4 w-48">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-amber-400" />
              <span className="text-xs font-semibold text-white/80">Roadmap Ready</span>
            </div>
            <p className="text-xs text-white/50">Analysis completed in</p>
            <p className="text-xl font-bold text-violet-400 mt-1">18 sec</p>
          </div>
        </div>

        <div className="absolute right-8 bottom-1/3 hidden lg:block animate-float" style={{ animationDelay: '4s' }}>
          <div className="glass-card rounded-2xl p-3 w-40">
            <div className="flex items-center gap-1 mb-1">
              <TrendingUp size={12} className="text-emerald-400" />
              <span className="text-[10px] text-emerald-400 font-semibold">+2.9 score boost</span>
            </div>
            <p className="text-[10px] text-white/40">Average after 30 days</p>
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-center text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles size={12} className="text-violet-400" />
            <span className="text-xs font-semibold text-violet-300 uppercase tracking-wide">YOUR LOOK, YOUR EVERYTHING</span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-900 leading-[1.05] tracking-tight mb-6 text-balance">
            Unlock Your<br />
            <span className="gradient-text">Best Look</span><br />
            With AI
          </h1>

          <p className="text-lg sm:text-xl text-white/60 max-w-2xl mb-10 leading-relaxed">
            Upload a selfie and get your personalized glow-up roadmap in seconds. 
            AI-powered attractiveness score, facial analysis, and actionable improvement plan.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4 mb-12">
            <button
              onClick={handleAnalyze}
              className="group flex items-center gap-3 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-lg px-8 py-4 rounded-full transition-all duration-300 active:scale-95 animate-pulse-glow w-full sm:w-auto justify-center">
              
              <Camera size={20} />
              Analyze My Face
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm font-medium" onClick={() => setShowHowItWorks(true)}>
              <Play size={14} className="text-violet-400" />
              See how it works
            </button>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/40">
            <div className="flex items-center gap-1.5">
              <Shield size={14} className="text-emerald-400" />
              <span>Photos encrypted</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap size={14} className="text-amber-400" />
              <span>Results in 18 seconds</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Users size={14} className="text-violet-400" />
              <span>127,000+ men analyzed</span>
            </div>
          </div>
        </div>

        {/* Hero image grid */}
        <div className="relative z-10 mt-16 w-full max-w-5xl mx-auto">
<div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[
            { src: "/assets/images/Screenshot_2026-03-22_005417-1774134161831.png", alt: 'Man with high attractiveness score after glow-up transformation', score: '8.4', delay: 0 },
            { src: "/assets/images/Screenshot_2026-03-22_005136-1774134185188.png", alt: 'Well-groomed man with defined jawline after style upgrade', score: '9.2', delay: 100 },
            { src: "/assets/images/Screenshot_2026-03-22_125207-1774176745432.png", alt: 'Man with sharp haircut and confident appearance after transformation', score: '7.6', delay: 200 }].
            map((item, i) =>
            <div
              key={i}
              className="relative rounded-2xl overflow-hidden group cursor-pointer"
              style={{ animationDelay: `${item.delay}ms` }}>
              
                <AppImage
                src={item.src}
                alt={item.alt}
                width={280}
                height={360}
                className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-500" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, j) =>
                    <Star key={j} size={8} className="fill-amber-400 text-amber-400" />
                    )}
                    </div>
                    <span className="text-sm font-bold text-amber-400 font-mono-data">{item.score}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#050508] to-transparent pointer-events-none" />
        </div>

        {/* Stats bar */}
        <div className="relative z-10 mt-8 w-full max-w-3xl mx-auto grid grid-cols-3 gap-4">
          {[
          { value: 127000, suffix: '+', label: 'Men Analyzed' },
          { value: 94, suffix: '%', label: 'Satisfaction Rate' },
          { value: 9.7, suffix: '/10', label: 'Avg Score Improvement', isDecimal: true }].
          map((stat, i) =>
          <div key={i} className="glass-card rounded-2xl p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold font-display gradient-text-gold">
                {stat.isDecimal ? stat.value : <CounterAnimation end={stat.value} />}{stat.suffix}
              </p>
              <p className="text-xs text-white/40 mt-1">{stat.label}</p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-950/5 to-transparent pointer-events-none" />
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 rounded-full px-4 py-1.5 mb-4">
              <Zap size={12} className="text-violet-400" />
              <span className="text-xs font-semibold text-violet-300 uppercase tracking-wide">Your Arsenal</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-800 mb-4">
              Everything you need to<br /><span className="gradient-text">dominate your looks</span>
            </h2>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Our AI analyzes 47 facial metrics to give you a complete, personalized male appearance upgrade plan.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((feature, i) =>
            <div
              key={i}
              className="glass-card rounded-2xl p-6 hover:border-violet-500/20 transition-all duration-300 group cursor-pointer">
              
                <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                  <feature.icon size={22} className={feature.color} />
                </div>
                <h3 className="font-display font-700 text-lg mb-2">{feature.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            )}
          </div>

          <div className="flex justify-center mt-10">
            <button className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm font-medium" onClick={() => setShowHowItWorks(true)}>
              <Play size={14} className="text-violet-400" />
              See how it works
            </button>
          </div>
        </div>
      </section>

      {/* Before / After Transformations */}
      <section id="transformations" className="py-24 px-4">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-4">
              <TrendingUp size={12} className="text-amber-400" />
              <span className="text-xs font-semibold text-amber-300 uppercase tracking-wide">Real Male Transformations</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-800 mb-4">
              What 30 days of<br /><span className="gradient-text-gold">discipline looks like</span>
            </h2>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Real men, real results. These transformations happened by following personalized LookMax AI roadmaps.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
            {BEFORE_AFTERS.map((item, i) =>
            <div key={i} className="glass-card rounded-2xl overflow-hidden group">
                <div className="relative">
                  {i === 0 || i === 1 ? (
                    <div className="relative">
                      <AppImage src={item.beforeImg} alt={item.beforeAlt} width={600} height={400} className="w-full object-cover" />
                    </div>
                  ) : (
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <AppImage src={item.beforeImg} alt={item.beforeAlt} width={150} height={200} className="w-full aspect-[3/4] object-cover" />
                      <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] text-white/70 font-semibold">BEFORE</div>
                      <div className="absolute bottom-2 left-2 text-sm font-bold text-white/60 font-mono-data">{item.scoreBefore}</div>
                    </div>
                    <div className="relative">
                      <AppImage src={item.afterImg} alt={item.afterAlt} width={150} height={200} className="w-full aspect-[3/4] object-cover" />
                      <div className="absolute top-2 right-2 bg-violet-600/80 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] text-white font-semibold">AFTER</div>
                      <div className="absolute bottom-2 right-2 text-sm font-bold text-amber-400 font-mono-data">{item.scoreAfter}</div>
                    </div>
                  </div>
                  )}
                  {i !== 0 && i !== 1 && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="bg-violet-600 rounded-full w-8 h-8 flex items-center justify-center border-2 border-[#050508]">
                      <ArrowRight size={12} className="text-white" />
                    </div>
                  </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sm">{item.name}, {item.age}</p>
                      <p className="text-[11px] text-white/40">{item.tag}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-400 font-mono-data">{item.improvement}</p>
                      <p className="text-[10px] text-white/40">score boost</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-950/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl sm:text-5xl font-800 mb-4">
              Trusted by <span className="gradient-text">127,000+ men</span>
            </h2>
            <p className="text-white/50 text-lg">Don't take our word for it. Here's what they're saying.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TESTIMONIALS.map((t, i) =>
            <div key={i} className="glass-card rounded-2xl p-5 hover:border-violet-500/20 transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <AppImage src={t.avatar} alt={t.avatarAlt} width={40} height={40} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-white/40">{t.handle}</p>
                  </div>
                  <div className="ml-auto">
                    <span className="text-sm font-bold text-amber-400 font-mono-data">{t.score}</span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) =>
                <Star key={j} size={10} className="fill-amber-400 text-amber-400" />
                )}
                </div>
                <p className="text-sm text-white/60 leading-relaxed">"{t.text}"</p>
              </div>
            )}
          </div>

          <div className="flex justify-center mt-10">
            <button className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm font-medium" onClick={() => setShowHowItWorks(true)}>
              <Play size={14} className="text-violet-400" />
              See how it works
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-4">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-4">
              <Award size={12} className="text-amber-400" />
              <span className="text-xs font-semibold text-amber-300 uppercase tracking-wide">Simple Pricing</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-800 mb-4">
              One price. <span className="gradient-text">Full access.</span>
            </h2>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Unlock your complete AI analysis, personalized male roadmap, and all future analyses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Monthly */}
            <div className="glass-card rounded-2xl p-8 border border-white/8 hover:border-white/15 transition-all duration-300">
              <h3 className="font-display font-700 text-xl mb-1">Monthly</h3>
              <p className="text-white/40 text-sm mb-6">Flexible, cancel anytime</p>
              <div className="mb-6">
                <span className="text-5xl font-bold font-display">€20</span>
                <span className="text-white/40 text-sm ml-2">/ month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Full attractiveness analysis', 'Personalized glow-up roadmap', 'Hairstyle & grooming tips', 'Skincare routine for men', 'Style recommendations', 'Dating profile optimizer'].map((feat) =>
                <li key={feat} className="flex items-center gap-2 text-sm text-white/70">
                    <Check size={14} className="text-violet-400 shrink-0" />
                    {feat}
                  </li>
                )}
              </ul>
              <button
                onClick={handleAnalyze}
                className="w-full py-3 rounded-xl border border-white/15 hover:border-violet-500/50 text-white font-semibold text-sm transition-all duration-200 active:scale-95">
                
                Start Monthly
              </button>
            </div>

            {/* Yearly — Best Value */}
            <div className="relative rounded-2xl p-8 bg-gradient-to-br from-violet-600/20 to-violet-900/20 border border-violet-500/40 glow-violet hover:glow-violet transition-all duration-300">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-amber-500 to-amber-400 text-black text-xs font-bold px-4 py-1 rounded-full">
                  ✦ BEST VALUE
                </span>
              </div>
              <h3 className="font-display font-700 text-xl mb-1">Yearly</h3>
              <p className="text-white/40 text-sm mb-6">3 months free included</p>
              <div className="mb-6">
                <span className="text-5xl font-bold font-display">€15</span>
                <span className="text-white/40 text-sm ml-2">/ month</span>
                <p className="text-xs text-white/40 mt-1">€180 billed annually</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['Full attractiveness analysis', 'Personalized glow-up roadmap', 'Hairstyle & grooming tips', 'Skincare routine for men', 'Style recommendations', 'Dating profile optimizer', 'Unlimited re-analyses', 'Priority AI processing'].map((feat) =>
                <li key={feat} className="flex items-center gap-2 text-sm text-white/80">
                    <Check size={14} className="text-amber-400 shrink-0" />
                    {feat}
                  </li>
                )}
              </ul>
              <button
                onClick={handleAnalyze}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-sm transition-all duration-200 active:scale-95">
                
                Unlock My Results
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-800 mb-4">Frequently asked questions</h2>
            <p className="text-white/50">Everything you need to know before getting started.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) =>
            <div key={i} className="glass-card rounded-2xl overflow-hidden">
                <button
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/2 transition-colors"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                
                  <span className="font-semibold text-sm sm:text-base pr-4">{faq.q}</span>
                  {openFaq === i ? <ChevronUp size={18} className="text-violet-400 shrink-0" /> : <ChevronDown size={18} className="text-white/40 shrink-0" />}
                </button>
                {openFaq === i &&
              <div className="px-6 pb-6">
                    <p className="text-sm text-white/60 leading-relaxed">{faq.a}</p>
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-violet-950/20 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-violet-600/10 blur-[100px] pointer-events-none" />
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-800 mb-6">
            Stop guessing.<br /><span className="gradient-text">Start leveling up.</span>
          </h2>
          <p className="text-white/50 text-lg mb-10">
            Join 127,000+ men who already know their score and are following their personalized roadmap.
          </p>
          <button
            onClick={handleAnalyze}
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-lg px-10 py-5 rounded-full transition-all duration-300 active:scale-95 animate-pulse-glow">
            
            <Camera size={20} />
            Get My Score — It's Free
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-xs text-white/30 mt-4">No credit card required to start. Upload & analyze for free.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-4">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={40} />
            <span className="font-display font-bold text-base">LookMax AI</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="/affiliate/register" className="hover:text-white transition-colors text-violet-400 hover:text-violet-300 font-medium">Referral Program</a>
            <a href="mailto:lookmaxers.ai@gmail.com" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p className="text-sm text-white/30">© 2026 LookMax AI. All rights reserved.</p>
        </div>
      </footer>

      {/* How It Works Modal */}
      {showHowItWorks &&
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setShowHowItWorks(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
          className="relative bg-[#0d0d14] border border-white/10 rounded-3xl p-6 sm:p-8 w-full max-w-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}>

            {/* Close button */}
            <button
            onClick={() => setShowHowItWorks(false)}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors">

              <X size={20} />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="font-display sm:text-3xl font-bold text-white text-xl">From selfie to personalized roadmap in minutes</h2>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-2 gap-4 -mt-5">
              {/* Step 1 */}
              <div className="flex flex-col items-center text-center bg-white/5 rounded-2xl p-4 border border-white/5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-600/20 border border-violet-500/30 flex items-center justify-center mb-3">
                  <Camera size={18} className="text-violet-400" />
                </div>
                <div>
                  <span className="text-xs font-bold text-violet-400 uppercase tracking-wider block mb-1">Step 1</span>
                  <h3 className="text-sm font-bold text-white mb-1">Upload Photo</h3>
                  <p className="text-xs text-white/50 leading-relaxed">Take a clear, front-facing selfie in good lighting. No filters needed.</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center text-center bg-white/5 rounded-2xl p-4 border border-white/5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mb-3">
                  <Brain size={18} className="text-amber-400" />
                </div>
                <div>
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-wider block mb-1">Step 2</span>
                  <h3 className="text-sm font-bold text-white mb-1">Analysis Runs</h3>
                  <p className="text-xs text-white/50 leading-relaxed">Our AI scans 47 facial harmony metrics to build your complete profile.</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center text-center bg-white/5 rounded-2xl p-4 border border-white/5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mb-3">
                  <Sparkles size={18} className="text-emerald-400" />
                </div>
                <div>
                  <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider block mb-1">Step 3</span>
                  <h3 className="text-sm font-bold text-white mb-1">Roadmap</h3>
                  <p className="text-xs text-white/50 leading-relaxed">A personalized 180-day plan with hairstyle, skincare, jawline, and style guide.</p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex flex-col items-center text-center bg-white/5 rounded-2xl p-4 border border-white/5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-sky-500/20 border border-sky-500/30 flex items-center justify-center mb-3">
                  <TrendingUp size={18} className="text-sky-400" />
                </div>
                <div>
                  <span className="text-xs font-bold text-sky-400 uppercase tracking-wider block mb-1">Step 4</span>
                  <h3 className="text-sm font-bold text-white mb-1">Start Today</h3>
                  <p className="text-xs text-white/50 leading-relaxed">View your full score, roadmap, and suggested changes. Start today!</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
            onClick={() => {setShowHowItWorks(false);handleAnalyze();}}
            className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-sm px-6 py-4 rounded-full transition-all duration-200 active:scale-95 mt-5">

              <Camera size={16} />
              Analyze My Face Now
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      }
    </div>);

}